import React from 'react';
import { useGeckoFormContext, useDidUpdateEffect } from './hooks';
import {
  Password,
  Search,
  SearchSelect,
  Select,
  SelectMulti,
  Telephone,
  Text,
  Textarea,
  Toggle,
  SelectMultiButtons,
  DateRangePicker,
} from './fields';
import './form-field.scss';

const fieldTypes = {
  password: Password,
  search: Search,
  search_select: SearchSelect,
  select: Select,
  select_multi: SelectMulti,
  telephone: Telephone,
  text: Text,
  textarea: Textarea,
  toggle: Toggle,
  select_multi_buttons: SelectMultiButtons,
  dateRange: DateRangePicker,
};

const RequiredIcon = () => <span className={'field-required'}>*</span>;

const FieldError = ({ error }) => (
  <span className={'field-error'}>{error}</span>
);

const GeckoFormField = ({
  name,
  label,
  description,
  fieldType,
  value,
  onChange,
  required = false,
  requiredError,
  width,
  error,
  onValueChange,
  showErrorAsTooltip = false,
  containerStyle = {},
  renderCustomComponent,
  ...props
}) => {
  const fieldData = useGeckoFormContext(name);
  const hydratedValue = fieldData ? fieldData.value : value;
  useDidUpdateEffect(onValueChange, hydratedValue);

  const getError = () => {
    if (error) return error;
    if (requiredError) return 'Required';
    if (!fieldData) return null;
    if (fieldData.error && (fieldData.hasTouched || fieldData.hasTriedToSubmit))
      return fieldData.error;
    if (
      fieldData.requiredError &&
      (fieldData.hasTouched || fieldData.hasTriedToSubmit)
    )
      return 'Required';
    return null;
  };

  const hydratedFieldData = {
    value: hydratedValue,
    onChange: onChange || fieldData?.handleChange,
    required: required || fieldData?.required,
    error: getError(),
  };

  const FieldComponent = fieldTypes[fieldType];

  const fieldErrorClass = hydratedFieldData.error ? ' field-error-border' : '';
  const className = `field-input${fieldErrorClass} ${fieldType}`;

  const styles = {
    width: width || '100%',
    ...containerStyle,
  };

  const renderFieldComponent = () => {
    if (renderCustomComponent) {
      return renderCustomComponent();
    }
    if (showErrorAsTooltip) {
      const inputClassName = `field-input ${fieldType}`;
      return (
        <FieldComponent
          name={name}
          value={hydratedFieldData.value}
          onChange={hydratedFieldData.onChange}
          error={error}
          wrapperClassName={fieldErrorClass}
          inputClassName={inputClassName}
          hasTooltip
          {...props}
        />
      );
    }
    return (
      <FieldComponent
        name={name}
        value={hydratedFieldData.value}
        onChange={hydratedFieldData.onChange}
        className={className}
        {...props}
      />
    );
  };

  return (
    <div className={'GeckoFormField form-group col-12'} style={styles}>
      {label && (
        <label className={'field-label'} htmlFor={name}>
          {label}
          {hydratedFieldData.required && <RequiredIcon />}
        </label>
      )}
      {description && <p className={'field-description'}>{description}</p>}
      {renderFieldComponent()}
      {hydratedFieldData.error && !showErrorAsTooltip && (
        <FieldError error={hydratedFieldData.error} />
      )}
    </div>
  );
};

export default GeckoFormField;
